class LoadMore extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    this.grid = document.getElementById('product-grid');
    this.gridContainer = document.getElementById('ProductGridContainer');
    this.isLoading = false;
    this.pagesLoaded = 1;
    this.restoreInProgress = false;
    this.hasAttemptedRestore = false;
    this.storageKey = this.getStorageKey();
    this.activeStorageKey = 'collection-return-active';
    this.previousScrollRestoration = null;
    this.previousScrollBehavior = '';
    this.handleGridClick = this.handleGridClick.bind(this);
    this.handlePageShow = this.handlePageShow.bind(this);
    this.handlePageHide = this.handlePageHide.bind(this);

    if (this.button) {
      this.button.addEventListener('click', this.loadNextPage.bind(this));
    }

    if (this.grid) {
      this.grid.addEventListener('click', this.handleGridClick);
    }

    window.addEventListener('pageshow', this.handlePageShow);
    window.addEventListener('pagehide', this.handlePageHide);

    this.restoreCollectionState();
  }

  disconnectedCallback() {
    if (this.grid) {
      this.grid.removeEventListener('click', this.handleGridClick);
    }

    window.removeEventListener('pageshow', this.handlePageShow);
    window.removeEventListener('pagehide', this.handlePageHide);
  }

  isMobileViewport() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  getStorageKey() {
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.delete('page');

    const queryString = url.searchParams.toString();
    return `collection-return:${url.pathname}${queryString ? `?${queryString}` : ''}`;
  }

  getNavigationType() {
    const navigationEntry = window.performance.getEntriesByType('navigation')[0];

    if (navigationEntry && navigationEntry.type) {
      return navigationEntry.type;
    }

    if (window.performance.navigation) {
      return window.performance.navigation.type === 2 ? 'back_forward' : 'navigate';
    }

    return 'navigate';
  }

  normalizeUrl(url) {
    try {
      const parsedUrl = new URL(url, window.location.origin);
      return `${parsedUrl.pathname}${parsedUrl.search}`;
    } catch (error) {
      return '';
    }
  }

  isProductLink(anchor) {
    return this.normalizeUrl(anchor.href).startsWith('/products/');
  }

  readCollectionState() {
    try {
      const storedState = sessionStorage.getItem(this.storageKey);
      return storedState ? JSON.parse(storedState) : null;
    } catch (error) {
      return null;
    }
  }

  saveCollectionState(overrides = {}) {
    try {
      const currentState = this.readCollectionState() || {};
      const state = {
        collectionUrl: this.normalizeUrl(window.location.href),
        nextPageUrl: this.button?.dataset?.href || null,
        pagesLoaded: this.pagesLoaded,
        scrollY: window.scrollY,
        timestamp: Date.now(),
        ...currentState,
        ...overrides,
      };

      sessionStorage.setItem(this.storageKey, JSON.stringify(state));
    } catch (error) {
      console.warn('Unable to save collection state.', error);
    }
  }

  markCollectionReturnActive() {
    try {
      sessionStorage.setItem(this.activeStorageKey, this.storageKey);
    } catch (error) {
      console.warn('Unable to mark collection return state.', error);
    }
  }

  clearCollectionReturnActive() {
    try {
      if (sessionStorage.getItem(this.activeStorageKey) === this.storageKey) {
        sessionStorage.removeItem(this.activeStorageKey);
      }
    } catch (error) {
      console.warn('Unable to clear collection return state.', error);
    }
  }

  clearCollectionState() {
    try {
      sessionStorage.removeItem(this.storageKey);
    } catch (error) {
      console.warn('Unable to clear collection state.', error);
    }
  }

  handleGridClick(event) {
    if (
      event.defaultPrevented ||
      (typeof event.button === 'number' && event.button !== 0) ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const anchor = event.target.closest('a[href]');

    if (!anchor || !this.grid?.contains(anchor) || !this.isProductLink(anchor)) {
      return;
    }

    const targetCard = anchor.closest('.grid__item');

    this.saveCollectionState({
      productUrl: this.normalizeUrl(anchor.href),
      cardViewportTop: targetCard ? targetCard.getBoundingClientRect().top : null,
      scrollY: window.scrollY,
    });
    this.markCollectionReturnActive();
  }

  handlePageShow(event) {
    this.hasAttemptedRestore = false;
    this.restoreCollectionState({ force: event.persisted });
  }

  handlePageHide() {
    const storedState = this.readCollectionState();

    if (!storedState?.productUrl) {
      return;
    }

    this.saveCollectionState({
      scrollY: window.scrollY,
    });
  }

  findProductCard(productUrl) {
    const productLink = Array.from(this.grid?.querySelectorAll('a[href]') || []).find((anchor) => {
      return this.normalizeUrl(anchor.href) === productUrl;
    });

    return productLink?.closest('.grid__item') || productLink || null;
  }

  isCardPositionClose(targetCard, storedCardViewportTop, threshold = 24) {
    if (!targetCard || !Number.isFinite(storedCardViewportTop)) {
      return false;
    }

    return Math.abs(targetCard.getBoundingClientRect().top - storedCardViewportTop) <= threshold;
  }

  isScrollClose(storedScrollY, threshold = 48) {
    if (!Number.isFinite(storedScrollY)) {
      return false;
    }

    return Math.abs(window.scrollY - storedScrollY) <= threshold;
  }

  setManualScrollRestoration() {
    if (!window.history || !('scrollRestoration' in window.history)) {
      return;
    }

    this.previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
  }

  restoreNativeScrollRestoration() {
    if (!window.history || !('scrollRestoration' in window.history)) {
      return;
    }

    window.history.scrollRestoration = this.previousScrollRestoration || 'auto';
    this.previousScrollRestoration = null;
  }

  setManualScrollBehavior() {
    this.previousScrollBehavior = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';
  }

  restoreNativeScrollBehavior() {
    if (this.previousScrollBehavior) {
      document.documentElement.style.scrollBehavior = this.previousScrollBehavior;
    } else {
      document.documentElement.style.removeProperty('scroll-behavior');
    }

    this.previousScrollBehavior = '';
  }

  setRestoringState(isRestoring) {
    document.documentElement.classList.toggle('collection-return-restoring', isRestoring);

    if (!this.gridContainer) {
      return;
    }

    if (isRestoring) {
      this.gridContainer.style.opacity = '0';
      this.gridContainer.style.pointerEvents = 'none';
      return;
    }

    this.gridContainer.style.removeProperty('opacity');
    this.gridContainer.style.removeProperty('pointer-events');
  }

  waitForNextPaint() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });
  }

  applyScrollPosition(storedState, targetCard) {
    const storedScrollY = Number(storedState?.scrollY);
    const storedCardViewportTop = Number(storedState?.cardViewportTop);

    if (!Number.isFinite(storedScrollY) && !targetCard) {
      return;
    }

    if (targetCard && Number.isFinite(storedCardViewportTop)) {
      const targetTop = window.scrollY + targetCard.getBoundingClientRect().top - storedCardViewportTop;
      const maxScrollY = Math.max(document.documentElement.scrollHeight - window.innerHeight, 0);
      window.scrollTo(0, Math.max(0, Math.min(targetTop, maxScrollY)));
      return;
    }

    if (Number.isFinite(storedScrollY)) {
      const maxScrollY = Math.max(document.documentElement.scrollHeight - window.innerHeight, 0);
      window.scrollTo(0, Math.min(storedScrollY, maxScrollY));
      return;
    }

    targetCard.scrollIntoView({ block: 'start', inline: 'nearest' });
  }

  async restoreCollectionState(options = {}) {
    if (this.hasAttemptedRestore || this.restoreInProgress || !this.grid) {
      return;
    }

    this.hasAttemptedRestore = true;

    if (!options.force && this.getNavigationType() !== 'back_forward') {
      return;
    }

    const storedState = this.readCollectionState();

    if (!storedState?.productUrl) {
      return;
    }

    this.restoreInProgress = true;
    this.setManualScrollRestoration();
    this.setManualScrollBehavior();
    this.setRestoringState(true);

    try {
      let targetCard = this.findProductCard(storedState.productUrl);
      const desiredPages = Math.max(1, Number(storedState.pagesLoaded) || 1);
      const storedScrollY = Number(storedState.scrollY);
      const storedCardViewportTop = Number(storedState.cardViewportTop);
      let safetyCounter = 0;

      if (
        options.force &&
        targetCard &&
        (this.isCardPositionClose(targetCard, storedCardViewportTop) || this.isScrollClose(storedScrollY))
      ) {
        return;
      }

      while (!targetCard && this.button?.dataset?.href && this.pagesLoaded < desiredPages && safetyCounter < 20) {
        safetyCounter += 1;
        const loaded = await this.loadNextPage({ isRestoring: true });

        if (!loaded) {
          break;
        }

        targetCard = this.findProductCard(storedState.productUrl);
      }

      while (!targetCard && this.button?.dataset?.href && safetyCounter < 20) {
        safetyCounter += 1;
        const loaded = await this.loadNextPage({ isRestoring: true });

        if (!loaded) {
          break;
        }

        targetCard = this.findProductCard(storedState.productUrl);
      }

      window.scrollTo(0, 0);
      await this.waitForNextPaint();
      this.applyScrollPosition(storedState, targetCard);
      await this.waitForNextPaint();
    } finally {
      this.setRestoringState(false);
      this.restoreInProgress = false;
      this.restoreNativeScrollRestoration();
      this.restoreNativeScrollBehavior();
      this.clearCollectionReturnActive();
      this.clearCollectionState();
    }
  }

  async loadNextPage(options = {}) {
    if (this.isLoading) {
      return false;
    }

    const url = this.button?.dataset?.href;

    if (!url) {
      this.button.classList.add('hidden');
      return false;
    }

    this.isLoading = true;
    this.button.classList.add('loading');
    const spinner = this.button.querySelector('.loading-overlay__spinner');
    spinner.classList.remove('hidden');

    try {
      const response = await fetch(url);
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const sourceGrid = html.getElementById('product-grid');

      if (!sourceGrid) {
        throw new Error('Product grid not found in response.');
      }

      const sourceItems = Array.from(sourceGrid.querySelectorAll('.grid__item'));

      if (this.grid) {
        const fragment = document.createDocumentFragment();
        const shouldAnimateItems = this.isMobileViewport() && !options.isRestoring;

        sourceItems.forEach((item) => {
          item.classList.add('scroll-trigger--cancel');
          item.querySelectorAll('.scroll-trigger').forEach((element) => {
            element.classList.add('scroll-trigger--cancel');
          });

          if (shouldAnimateItems) {
            item.classList.add('load-more-item-enter');
          }

          fragment.appendChild(item);
        });

        this.grid.appendChild(fragment);

        if (shouldAnimateItems) {
          requestAnimationFrame(() => {
            sourceItems.forEach((item) => {
              item.classList.add('load-more-item-enter-active');
            });
          });

          window.setTimeout(() => {
            sourceItems.forEach((item) => {
              item.classList.remove('load-more-item-enter');
              item.classList.remove('load-more-item-enter-active');
            });
          }, 280);
        }

        if (typeof bindExternalVariants === 'function') {
          sourceItems.forEach((item) => {
            bindExternalVariants(item);
          });
        }
      }

      try {
        if (typeof yotpo !== 'undefined' && yotpo.initialized) {
          const api = new Yotpo.API(yotpo);
          api.refreshWidgets();
        }
      } catch (error) {
        console.warn('Yotpo refresh failed:', error);
      }

      this.pagesLoaded += 1;

      const nextButton = html.querySelector('.load-more-btn');

      if (nextButton && nextButton.dataset.href) {
        this.button.dataset.href = nextButton.dataset.href;
        this.button.classList.remove('hidden');
      } else {
        this.button.classList.add('hidden');
        this.button.removeAttribute('data-href');
      }

      this.saveCollectionState();

      return sourceItems.length > 0;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      this.isLoading = false;
      this.button.classList.remove('loading');
      spinner.classList.add('hidden');
    }
  }
}

customElements.define('load-more', LoadMore);
